// ── #675, GATE 675 D7-D12, Session 79.22b.5.4b -- SORTING AND NARROWING THE
// TRANSFER TABS ──────────────────────────────────────────────────────────────
//
// What a person sees: Houston's fabrication manager opens Filter & sort on To
// make and puts the list in due-date order, or taps Late, or narrows it to
// what is for Austin. Dallas's manager taps "Coming to Dallas" and every tab
// shows only what is coming to Dallas, each tab's count recounting.
//
// THE RULES ARE HERE, PURE, so one test file can walk every one of them, and
// the page only asks. Sorting and narrowing the WORK IN HAND happens on the
// page, over lists it already holds (gate 675 D15 loads every open transfer);
// Finished's narrowing is the database's, through find_branch_transfers()'s
// own p_from, p_to, p_ended, p_since and p_until (D11), so a page of 25 is a
// page of the narrowed list rather than 25 narrowed after the fact.
//
// Filters narrow what a SEARCH left, never the other way round, so a count
// pill keeps meaning "what is on this tab now".

import { promisedDateFor } from './branchTransfers';

// ── D7 -- EACH TAB'S ORDERS, today's order FIRST, so nobody's list moves the
// day this ships. Finished is the one default that changes: "newest asked" put
// a transfer that finished this morning below ones that finished last week.
export const SORT_LABELS = {
  newest: 'Newest first',
  waiting: 'Waiting longest',
  due: 'Due soonest',
  road: 'On the road longest',
  finished_recent: 'Finished most recently',
  finished_oldest: 'Finished longest ago',
};

export const TAB_SORTS = {
  confirm: ['newest', 'waiting', 'due'],
  to_make: ['waiting', 'due', 'newest'],
  to_load: ['newest', 'due', 'waiting'],
  moving: ['due', 'road', 'newest'],
  collect: ['due', 'road', 'newest'],
  count_in: ['due', 'road', 'newest'],
  finished: ['finished_recent', 'finished_oldest'],
};

// D8 -- the key each person's choices are saved under, on their account
// (users.ui_preferences, #607's rule: the account, never the device, because
// D&R shares tablets).
export const SORT_PREFERENCE_KEY = 'transfers_sort';

// A tab's order: the saved choice when it is still one of the tab's own, else
// the tab's default. A saved value from a later or earlier build that this tab
// does not offer falls back rather than sorting by nothing.
export function sortFor(tabId, saved) {
  const offered = TAB_SORTS[tabId] || [];
  const v = saved && typeof saved === 'object' ? saved[tabId] : null;
  return offered.includes(v) ? v : (offered[0] || null);
}

// What Finished's database read is asked for: 'recent' or 'oldest'.
export function finishedOrder(sortId) {
  return sortId === 'finished_oldest' ? 'oldest' : 'recent';
}

// WHICH READ A PAGE OF FINISHED BELONGS TO: the branches it was read for, the
// search, the order and the filters. A page read for any other is never drawn,
// so a change never shows the previous answer's cards while the new one loads
// (the rule Grok's review gave the search at 79.22b.5.4, kept for sort and
// filters). `scope` is the branch in view, or 'all' - Grok's review at
// 79.22b.5.4b found a branch change kept the previous branch's page on screen,
// tappable, under a heading already naming the new branch.
export function finishedReadKey(term, sortId, filters, scope = null) {
  return JSON.stringify([scope ?? null, term ?? null, sortId || null, filters || {}]);
}

// The read behind #752's waiting group belongs to one branch scope and one
// search the same way: rows read for another are never drawn (Grok, 79.22b.5.4b).
export function waitingReadKey(scope, term) {
  return JSON.stringify([scope ?? null, term ?? null]);
}

// ── DUE -- ONE RULE for sort, filter and card alike (gate 675 B1) ───────────
//
// The earliest promised date on an item still coming (promisedDateFor(): the
// item's own date, else the one the approver set for the whole ask), else the
// date it was needed by. A refused item promises nothing.
export function dueDateOf(transfer) {
  if (!transfer) return null;
  const lines = (transfer.branch_transfer_items || [])
    .filter(l => l?.line_status !== 'refused');
  const promised = lines.map(l => promisedDateFor(l, transfer)).filter(Boolean).sort();
  if (promised.length) return String(promised[0]).slice(0, 10);
  if (!lines.length && transfer.confirmed_date) return String(transfer.confirmed_date).slice(0, 10);
  return transfer.needed_by ? String(transfer.needed_by).slice(0, 10) : null;
}

const time = v => (v ? new Date(v).getTime() || 0 : 0);

// The transfer a row stands for: a transfer, a To make row, an owed row, or a
// truck (which carries it as __transfer once sortByPromised() has read it, or
// as branch_transfers straight off the load read).
export function transferOfRow(row) {
  if (!row) return null;
  if (row.transfer && typeof row.transfer === 'object') return row.transfer;
  if (row.__transfer) return row.__transfer;
  if (row.branch_transfers && typeof row.branch_transfers === 'object') return row.branch_transfers;
  return row;
}

// A truck (or a hub pickup drawn as one) names the transfer it carries.
const isTruck = row => row?.transfer_id != null;

// A sort is stable: equal rows keep the order they arrived in, which is the
// order every list had before this existed.
export function sortRows(rows, sortId) {
  const list = Array.isArray(rows) ? rows.map((r, i) => ({ r, i })) : [];
  const key = ({ r }) => {
    const t = transferOfRow(r);
    switch (sortId) {
      case 'newest': return -time(t?.requested_at);
      case 'waiting': return time(t?.requested_at);
      case 'road': return isTruck(r) && r.loaded_at ? time(r.loaded_at) : null;
      case 'due': {
        const d = dueDateOf(t);
        return d ? time(`${d}T00:00:00`) : null;
      }
      default: return 0;
    }
  };
  const keyed = list.map(x => ({ ...x, k: key(x) }));
  keyed.sort((a, b) => {
    // Nothing to sort by -- no date, no truck -- goes last, never first: an
    // undated row is not urgent, and sorting it to the top buries the ones
    // that are (sortByPromised()'s rule).
    if (a.k == null && b.k == null) return a.i - b.i;
    if (a.k == null) return 1;
    if (b.k == null) return -1;
    return a.k - b.k || a.i - b.i;
  });
  return keyed.map(x => x.r);
}

// ── D9, D10, D11 -- WHICH NARROWING EACH TAB OFFERS ────────────────────────
//
// Where the tab fixes one end, only the other is offered: To make and To load
// are always LEAVING the branch in view, so they offer To; To collect and To
// count in are always COMING to it, so they offer From. That holds only while
// one branch is in view - looking at every branch, both ends mean something on
// every tab. The two presets need one branch to name.
const LEAVING_TABS = ['to_make', 'to_load'];
const COMING_TABS = ['collect', 'count_in'];

export function filtersOffered(tabId, { singleBranch = true } = {}) {
  const finished = tabId === 'finished';
  return {
    from: !(singleBranch && LEAVING_TABS.includes(tabId)),
    to: !(singleBranch && COMING_TABS.includes(tabId)),
    presets: singleBranch && !LEAVING_TABS.includes(tabId) && !COMING_TABS.includes(tabId),
    due: !finished,
    when: finished,
    ended: finished,
  };
}

export const NO_FILTERS = Object.freeze({ from: null, to: null, due: null, when: null, ended: null });

// What applies on THIS tab: a filter the tab does not offer is not applied and
// not shown (F1), though it stays set and returns on a tab that offers it.
export function filtersApplied(filters, tabId, ctx = {}) {
  const f = { ...NO_FILTERS, ...(filters || {}) };
  const o = filtersOffered(tabId, ctx);
  return {
    from: o.from ? f.from : null,
    to: o.to ? f.to : null,
    due: o.due ? f.due : null,
    when: o.when ? f.when : null,
    ended: o.ended ? f.ended : null,
  };
}

export function anyApplied(applied) {
  return !!applied && Object.values(applied).some(v => v != null);
}

// ── D10 -- DUE ──────────────────────────────────────────────────────────────
export const DUE_CHOICES = [
  { id: null, label: 'Any date' },
  { id: 'late', label: 'Late' },
  { id: 'today', label: 'Due today' },
  { id: 'next7', label: 'Due in the next 7 days' },
  { id: 'next30', label: 'Due in the next 30 days' },
  { id: 'none', label: 'No date yet' },
];

// "Today" is the device's local date; Due is a date with no time, so a time
// zone never moves it (X2).
export function localDay(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
export function addDays(day, n) {
  const [y, m, d] = day.split('-').map(Number);
  return localDay(new Date(y, m - 1, d + n));
}

// Two date boxes, either may be empty; From after To swaps them (F3).
export function ordered(range) {
  const since = range?.since || null;
  const until = range?.until || null;
  return since && until && since > until ? { since: until, until: since } : { since, until };
}

// Late is due before today and not finished (Odoo's rule); the working tabs
// hold nothing finished. A transfer with no date at all is never late.
export function dueMatches(due, transfer, today = localDay()) {
  if (due == null) return true;
  const d = dueDateOf(transfer);
  if (due === 'none') return !d;
  if (!d) return false;
  if (due === 'late') return d < today;
  if (due === 'today') return d === today;
  if (due === 'next7') return d >= today && d <= addDays(today, 7);
  if (due === 'next30') return d >= today && d <= addDays(today, 30);
  if (typeof due === 'object') {
    const { since, until } = ordered(due);
    return (!since || d >= since) && (!until || d <= until);
  }
  return true;
}

// ── D9 -- FROM AND TO ───────────────────────────────────────────────────────
//
// A transfer: its own FROM and TO. A branch's own-shelf request has the one
// branch at both ends, so it is both coming and leaving (X2).
// A TRUCK: from the transfer's sending branch OR the truck's own leg - so a
// Houston transfer brought on from the Austin yard is kept by From Houston and
// by From Austin, and either way the card's own face says why (the number reads
// HOU-TRF, the card reads From Austin). Ruled with the plan at R1.
export function branchMatches({ from = null, to = null }, row) {
  if (from == null && to == null) return true;
  const t = transferOfRow(row);
  const truck = isTruck(row) ? row : null;
  const is = (a, b) => a != null && b != null && String(a) === String(b);
  const fromOk = from == null || is(t?.from_branch_id, from) || is(truck?.from_branch_id, from);
  const toOk = to == null || is(t?.to_branch_id, to) || is(truck?.to_branch_id, to);
  return fromOk && toOk;
}

// One list narrowed by everything that applies on its tab. Kept cards (a press
// just acted on them, #665 D21) stay whatever the filters say, as they do for
// a search: their answer and their Undo live on them.
export function keepFiltered(rows, applied, { today = localDay() } = {}) {
  if (!anyApplied(applied)) return Array.isArray(rows) ? rows : [];
  return (Array.isArray(rows) ? rows : []).filter(r => {
    if (r?.__pinned || r?.pinned) return true;
    // A reserve row on To make is stock running low, not a transfer: it has no
    // branch to narrow by and no date, so a filter never keeps one.
    if (r?.kind === 'reserve') return false;
    return branchMatches(applied, r) && dueMatches(applied.due, transferOfRow(r), today);
  });
}

// ── D11 -- FINISHED'S OWN DATE AND HOW IT ENDED ────────────────────────────
export const WHEN_CHOICES = [
  { id: null, label: 'Any time' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'year', label: 'This year' },
];

// Each label to the ending the database records (the five FINISHED_STATUSES).
export const ENDED_CHOICES = [
  { id: null, label: 'Any ending' },
  { id: 'received', label: 'Received' },
  { id: 'denied', label: 'Refused' },
  { id: 'cancelled', label: 'Withdrawn' },
  { id: 'closed_short', label: 'Closed short' },
  { id: 'closed_unfilled', label: 'Nothing more coming' },
];

const midnight = day => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toISOString();
};

// The local-day window as the database asks for it: since <= finished < until.
export function whenWindow(when, today = localDay()) {
  if (when == null) return { since: null, until: null };
  const [y, m] = today.split('-').map(Number);
  const first = (yy, mm) => localDay(new Date(yy, mm - 1, 1));
  switch (when) {
    case 'last7': return { since: midnight(addDays(today, -6)), until: null };
    case 'last30': return { since: midnight(addDays(today, -29)), until: null };
    case 'month': return { since: midnight(first(y, m)), until: null };
    case 'last_month': return { since: midnight(first(y, m - 1)), until: midnight(first(y, m)) };
    case 'year': return { since: midnight(first(y, 1)), until: null };
    default: {
      if (typeof when !== 'object') return { since: null, until: null };
      const { since, until } = ordered(when);
      return { since: since ? midnight(since) : null, until: until ? midnight(addDays(until, 1)) : null };
    }
  }
}

// What Finished's read and Finished's search count are given (D11), only the
// arguments that narrow, so a plain page asks exactly what it always has.
export function finishedArgs(applied, today = localDay()) {
  const a = applied || {};
  const out = {};
  if (a.from != null) out.p_from = a.from;
  if (a.to != null) out.p_to = a.to;
  if (a.ended) out.p_ended = [a.ended];
  const { since, until } = whenWindow(a.when, today);
  if (since) out.p_since = since;
  if (until) out.p_until = until;
  return out;
}

// ── F2 -- THE WORDS ─────────────────────────────────────────────────────────
export const FILTER_BUTTON = 'Filter & sort';
export const SHEET_TITLE = 'Filter and sort';
export const CLEAR_ALL = 'Clear all';
export const CLEAR_FILTERS = 'Clear filters';
export const PICK_DATES = 'Pick dates…';
export const ANY_BRANCH = 'Any branch';

export function liveCount(n) {
  if (n === 0) return 'No transfers';
  return n === 1 ? '1 transfer' : `${n} transfers`;
}
export function showButton(n) {
  if (n === 0) return 'Close';
  return n === 1 ? 'Show 1 transfer' : `Show ${n} transfers`;
}
export function filtersEmptySentence(tabLabel) {
  return `Nothing on ${tabLabel} matches these filters.`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function shortDay(day) {
  if (!day) return '';
  const [, m, d] = day.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}
export function rangeWords(range) {
  const { since, until } = ordered(range);
  if (since && until) return `${shortDay(since)} – ${shortDay(until)}`;
  if (since) return `From ${shortDay(since)}`;
  if (until) return `Until ${shortDay(until)}`;
  return '';
}

// The applied chips, each with what it removes. "Coming to {branch}" and
// "Leaving {branch}" are the presets read back: To or From set to the branch
// in view.
export function appliedChips(applied, { branchNames = {}, lookingAt = null, singleBranch = true } = {}) {
  const a = applied || {};
  const name = id => branchNames[String(id)] || 'that branch';
  const chips = [];
  const preset = singleBranch && lookingAt != null;
  if (a.from != null) {
    chips.push({ key: 'from', words: preset && String(a.from) === String(lookingAt) ? `Leaving ${name(a.from)}` : `From ${name(a.from)}` });
  }
  if (a.to != null) {
    chips.push({ key: 'to', words: preset && String(a.to) === String(lookingAt) ? `Coming to ${name(a.to)}` : `To ${name(a.to)}` });
  }
  if (a.due != null) {
    chips.push({ key: 'due', words: typeof a.due === 'object' ? `Due ${rangeWords(a.due)}` : DUE_CHOICES.find(c => c.id === a.due)?.label });
  }
  if (a.when != null) {
    chips.push({ key: 'when', words: typeof a.when === 'object' ? `Finished ${rangeWords(a.when)}` : WHEN_CHOICES.find(c => c.id === a.when)?.label });
  }
  if (a.ended != null) chips.push({ key: 'ended', words: ENDED_CHOICES.find(c => c.id === a.ended)?.label });
  return chips.filter(c => c.words);
}
